import type { JSX } from "react";
import type { Components, ExtraProps } from "react-markdown";

import React from "react";

import { Checkbox } from "@replo/design-system/components/checkbox/Checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@replo/design-system/components/table/Table";
import ChatToken from "@v2/features/chat/components/chat-elements/chat-tokens/ChatToken";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import { parseJsonOrNull } from "replo-utils/lib/json";
import { chatTokenSchema } from "schemas/chatMessage";

interface ColorChipProps {
  color: string;
}

function ColorChip({ color }: ColorChipProps) {
  return (
    <span
      className="inline-block align-middle mx-1 my-0.5 size-5 rounded outline outline-1 outline-slate-300"
      style={{ backgroundColor: color }}
      role="img"
      aria-label={`Color ${color}`}
    />
  );
}

interface MarkdownRendererProps {
  children: string;
  className?: string;
}
interface ElementNode {
  type: "element";
  tagName: string;
  properties?: Record<string, unknown>;
  children?: AstNode[];
  data?: Record<string, unknown>;
}

interface TextNode {
  type: "text";
  value?: string;
}

interface ParentNode {
  type: string;
  children: AstNode[];
}

type AstNode = ElementNode | TextNode | ParentNode;

const HEX_COLOR_REGEX = /#[\dA-Fa-f]{6}/g;

function isElementNode(node: AstNode): node is ElementNode {
  return node.type === "element";
}

function isTextNode(node: AstNode): node is TextNode {
  return node.type === "text";
}

function hasChildren(node: AstNode): node is ParentNode {
  return "children" in node && Array.isArray(node.children);
}

const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames ?? []),
    "br",
    "chat-token",
    "asset-component",
    "product-component",
    "color-chip",
  ],
  allowCustomElements: true,
  attributes: {
    ...defaultSchema.attributes,
    "chat-token": ["data-token"],
    "asset-component": ["data"],
    "product-component": ["data"],
    "color-chip": ["data-color"],
  },
};

const rehypeChatToken = () => (tree: AstNode) => {
  transformChatTokenNodes(tree);
};

const transformChatTokenNodes = (node: AstNode) => {
  if (isElementNode(node) && node.tagName === "chat-token") {
    const payload = readTokenPayload(node);
    if (payload) {
      node.children = [];
      node.data = {
        ...(node.data ?? {}),
        tokenPayload: payload,
      };
    }
  }

  if (hasChildren(node)) {
    node.children.forEach((child) => transformChatTokenNodes(child));
  }
};

const getHexColorMatches = (node: AstNode): RegExpMatchArray[] | null => {
  if (!isTextNode(node) || !node.value) {
    return null;
  }
  const matches = [...node.value.matchAll(HEX_COLOR_REGEX)];
  return matches.length > 0 ? matches : null;
};

const rehypeColorChip = () => (tree: AstNode) => {
  transformHexColors(tree);
};

const transformHexColors = (node: AstNode) => {
  if (isElementNode(node) && hasChildren(node)) {
    const transformedChildren: AstNode[] = [];

    for (const child of node.children) {
      transformHexColors(child);

      if (!isTextNode(child)) {
        transformedChildren.push(child);
        continue;
      }

      const matches = getHexColorMatches(child);
      if (matches && child.value) {
        const nodeSegments: AstNode[] = [];
        let lastIndex = 0;
        const textValue = child.value;

        for (const match of matches) {
          const matchIndex = match.index!;
          const hexColor = match[0];

          if (matchIndex > lastIndex) {
            nodeSegments.push({
              type: "text",
              value: textValue.slice(lastIndex, matchIndex),
            });
          }

          nodeSegments.push({
            type: "element",
            tagName: "color-chip",
            properties: {
              "data-color": hexColor,
            },
            children: [],
          });

          nodeSegments.push({
            type: "text",
            value: hexColor,
          });

          lastIndex = matchIndex + hexColor.length;
        }

        if (lastIndex < textValue.length) {
          nodeSegments.push({
            type: "text",
            value: textValue.slice(lastIndex),
          });
        }

        transformedChildren.push(...nodeSegments);
      } else {
        transformedChildren.push(child);
      }
    }

    node.children = transformedChildren;
  }

  if (!isElementNode(node) && hasChildren(node)) {
    node.children.forEach((child) => transformHexColors(child));
  }
};

export function MarkdownRenderer({
  children,
  className,
}: MarkdownRendererProps) {
  return (
    <Markdown
      remarkPlugins={[remarkGfm]}
      remarkRehypeOptions={{ allowDangerousHtml: true }}
      rehypePlugins={[
        rehypeRaw,
        rehypeChatToken,
        rehypeColorChip,
        [rehypeSanitize, sanitizeSchema],
      ]}
      components={COMPONENTS}
      className={className}
    >
      {children}
    </Markdown>
  );
}

const chatTokenRenderer = ({ node }: ExtraProps) => {
  if (
    !node ||
    !isElementNode(node as AstNode) ||
    (node as ElementNode).tagName !== "chat-token"
  ) {
    return null;
  }

  const element = node as ElementNode;
  const payload = retrieveTokenPayload(element);
  if (!payload) {
    return null;
  }

  const parsed = chatTokenSchema.safeParse(parseJsonOrNull(payload));
  if (!parsed.success) {
    return null;
  }

  return (
    <span className="inline-block align-baseline max-w-full">
      <ChatToken token={parsed.data} />
    </span>
  );
};

const colorChipRenderer = ({ node }: ExtraProps) => {
  if (
    !node ||
    !isElementNode(node as AstNode) ||
    (node as ElementNode).tagName !== "color-chip"
  ) {
    return null;
  }

  const element = node as ElementNode;
  const color =
    element.properties?.["data-color"] ?? element.properties?.dataColor;

  if (typeof color !== "string" || !color.match(/^#[\dA-Fa-f]{6}$/)) {
    return null;
  }

  return <ColorChip color={color} />;
};

type ChatTokenComponents = Components & {
  "chat-token": typeof chatTokenRenderer;
  "color-chip": typeof colorChipRenderer;
};

type Renderer = NonNullable<Components[keyof Components]>;

type WithClassOptions = {
  as?: keyof JSX.IntrinsicElements;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
};

const withClass = (
  tag: keyof JSX.IntrinsicElements,
  className?: string,
  { as, prefix, suffix }: WithClassOptions = {},
): Renderer => {
  const Component = ({
    node: _node,
    children,
    ...props
  }: React.ClassAttributes<HTMLElement> &
    React.HTMLAttributes<HTMLElement> &
    ExtraProps) => {
    const Tag = as ?? tag;
    const content: React.ReactNode[] = [];

    if (prefix !== undefined && prefix !== null) {
      content.push(prefix);
    }

    content.push(children);

    if (suffix !== undefined && suffix !== null) {
      content.push(suffix);
    }

    return React.createElement(Tag, { className, ...props }, ...content);
  };

  return Component as Renderer;
};

const COMPONENTS = {
  h1: withClass(
    "h1",
    "text-lg font-semibold leading-normal mb-3 mt-5 first:mt-0",
  ),
  h2: withClass(
    "h2",
    "text-base font-semibold leading-normal mb-2 mt-4 first:mt-0",
  ),
  h3: withClass(
    "h3",
    "text-sm font-semibold leading-normal mb-2 mt-4 first:mt-0",
  ),
  h4: withClass(
    "h4",
    "text-sm font-medium leading-normal mb-1.5 mt-3 first:mt-0",
  ),
  h5: withClass(
    "h5",
    "text-xs font-medium leading-normal mb-1.5 mt-3 first:mt-0",
  ),
  strong: withClass("strong", "font-semibold"),
  em: withClass("em", "italic"),
  a: withClass(
    "a",
    "text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary",
  ),
  blockquote: withClass(
    "blockquote",
    "border-l-2 border-muted pl-3 my-3 text-muted italic leading-normal",
  ),
  pre: ({ node: _node, children }) => (
    <pre className="bg-surface rounded-md px-3 py-2.5 my-3 overflow-x-auto">
      {children}
    </pre>
  ),
  code: ({ node: _node, children }) => {
    const isBlock = typeof children === "string" && children.includes("\n");

    if (isBlock) {
      return (
        <code className="font-mono text-[13px] leading-normal block whitespace-pre-wrap">
          {children}
        </code>
      );
    }

    return (
      <code className="bg-surface/80 px-1.5 py-0.5 rounded text-[0.9em] font-mono">
        {children}
      </code>
    );
  },
  ol: withClass("ol", "list-decimal space-y-1.5 pl-5 my-3 leading-normal"),
  ul: ({ node, children }) => {
    const isTaskList =
      node?.properties?.className &&
      Array.isArray(node.properties.className) &&
      node.properties.className.includes("contains-task-list");

    return (
      <ul
        className={
          isTaskList
            ? "space-y-1.5 my-3 list-none pl-0 leading-normal"
            : "list-disc space-y-1.5 pl-5 my-3 leading-normal"
        }
      >
        {children}
      </ul>
    );
  },
  li: ({ node, children }) => {
    const isTaskItem =
      node?.properties?.className &&
      Array.isArray(node.properties.className) &&
      node.properties.className.includes("task-list-item");

    return (
      <li
        className={
          isTaskItem
            ? "flex items-start gap-2 leading-normal"
            : "leading-normal"
        }
      >
        {children}
      </li>
    );
  },
  input: ({ node }) => {
    if (node?.properties?.type === "checkbox") {
      const checked = Boolean(node.properties.checked);
      return <Checkbox checked={checked} />;
    }
    return null;
  },
  table: ({ node: _node, children }) => (
    <div className="border border-border border-0.5 rounded-xl overflow-hidden">
      <Table>{children}</Table>
    </div>
  ),
  thead: ({ node: _node, children }) => <TableHeader>{children}</TableHeader>,
  tbody: ({ node: _node, children }) => <TableBody>{children}</TableBody>,
  tr: ({ node: _node, children }) => <TableRow>{children}</TableRow>,
  th: ({ node: _node, children }) => <TableHead>{children}</TableHead>,
  td: ({ node: _node, children }) => <TableCell>{children}</TableCell>,
  p: withClass("p", "leading-normal mb-3 last:mb-0"),
  hr: withClass("hr", "border-0 border-t border-border my-4"),
  "chat-token": chatTokenRenderer,
  "color-chip": colorChipRenderer,
} as ChatTokenComponents;

const extractAttributeValue = (element: ElementNode): string | null => {
  const properties = element.properties ?? {};
  const attributeValue =
    properties["data-token"] ?? properties.dataToken ?? null;

  if (typeof attributeValue === "string") {
    return attributeValue;
  }

  if (Array.isArray(attributeValue) && attributeValue.length > 0) {
    const first = attributeValue[0];
    if (typeof first === "string") {
      return first;
    }
  }

  return null;
};

const readTokenPayload = (element: ElementNode) => {
  const attributeValue = extractAttributeValue(element);
  if (attributeValue) {
    return decodePayload(attributeValue);
  }

  const legacyText = (element.children ?? [])
    .map((child) => (isTextNode(child) ? child.value ?? "" : ""))
    .join("")
    .trim();

  return legacyText ? decodePayload(legacyText) : null;
};

const retrieveTokenPayload = (element: ElementNode) => {
  const payloadFromData = element.data?.tokenPayload;
  if (typeof payloadFromData === "string" && payloadFromData.length > 0) {
    return payloadFromData;
  }

  return readTokenPayload(element);
};

const decodePayload = (value: string) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};
